import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MdOutlinePointOfSale } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { POINT } from "../../config/apiConfig";
import Modal from "../modals/Modal";
import { Badge, EmptyState, ErrorBlock, SkeletonRows } from "../crm/Primitives";
import { POINT_SETUP_TEXT } from "./eventHelpers";

/*
  Maquininhas da Relinq (Pagamentos > Maquininhas).

  Cadastra, lista e remove as maquininhas Mercado Pago Point que recebem os
  planos. É o mesmo cadastro que o salão faz no app, só que sempre na empresa da
  plataforma, que o SERVIDOR escolhe: nada daqui diz de qual empresa é.

  A maquininha precisa já estar vinculada à conta Mercado Pago da Relinq (pelo app
  do Mercado Pago). Aqui a equipe escolhe qual delas a Relinq pode cobrar e dá um
  apelido. Colocar a maquininha em modo PDV (o modo que recebe cobrança do
  sistema) é feito aqui, mas só depois de a pessoa confirmar, porque nesse modo
  ela deixa de vender sozinha pelo app do Mercado Pago, e é preciso reiniciá-la.

  A chave ["events", "point-terminals"] é a mesma que "Cobrar plano" lê, então
  cadastrar ou remover aqui já vale na cobrança sem recarregar a página.

  Erros e avisos aparecem DENTRO do modal (o aviso da página fica atrás do fundo
  borrado).
*/

const TERMINALS_KEY = ["events", "point-terminals"];
const AVAILABLE_KEY = ["events", "point-terminals-available"];

const campoClass =
  "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100";

const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400";

const serverMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const PlatformTerminalsModal = ({ onClose }) => {
  const queryClient = useQueryClient();

  const [adding, setAdding] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [confirmPdv, setConfirmPdv] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const terminals = useQuery(
    TERMINALS_KEY,
    async () => {
      const { data } = await axios.get(POINT.TERMINALS);
      return data.data;
    },
    { refetchInterval: false }
  );
  const info = terminals.data;
  const registered = info?.terminals || [];

  const available = useQuery(
    AVAILABLE_KEY,
    async () => {
      const { data } = await axios.get(POINT.AVAILABLE_TERMINALS);
      return Array.isArray(data?.data) ? data.data : [];
    },
    {
      enabled: adding,
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      cacheTime: 0,
      onError: (err) =>
        setError(serverMessage(err, "Não foi possível consultar as maquininhas da conta.")),
    }
  );

  // Só entra na escolha o que existe na conta e ainda é de ninguém.
  const choices = (available.data || []).filter(
    (item) => !item.registered && !item.unavailable
  );
  const chosen = choices.find((item) => item.mpTerminalId === selectedId);
  const needsPdv = Boolean(chosen) && chosen.operatingMode !== "PDV";

  const resetForm = () => {
    setAdding(false);
    setSelectedId("");
    setName("");
    setConfirmPdv(false);
  };

  const register = useMutation(
    async () => {
      const { data } = await axios.post(POINT.TERMINALS, {
        mpTerminalId: chosen.mpTerminalId,
        name: name.trim(),
        switchToPdv: needsPdv && confirmPdv,
      });
      return data?.data;
    },
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(TERMINALS_KEY);
        setError(null);
        setMessage(
          result?.restartRequired
            ? "Maquininha cadastrada. Reinicie a maquininha para ela entrar em modo PDV."
            : "Maquininha cadastrada."
        );
        resetForm();
      },
      onError: (err) =>
        setError(serverMessage(err, "Não foi possível cadastrar a maquininha.")),
    }
  );

  const remove = useMutation(
    (id) => axios.delete(`${POINT.TERMINAL}/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TERMINALS_KEY);
        setError(null);
        setMessage("Maquininha removida.");
      },
      onError: (err) =>
        setError(serverMessage(err, "Não foi possível remover a maquininha.")),
      onSettled: () => setRemovingId(null),
    }
  );

  const canRegister =
    Boolean(chosen) &&
    name.trim().length > 0 &&
    (!needsPdv || confirmPdv) &&
    !register.isLoading;

  const configured = Boolean(info?.configured);

  return (
    <Modal
      isOpen
      onClose={() => !register.isLoading && onClose()}
      size="md"
      icon={MdOutlinePointOfSale}
      title="Maquininhas da Relinq"
      subtitle="As maquininhas que recebem os planos no cartão"
    >
      <div className="space-y-4">
        {terminals.isLoading ? (
          <SkeletonRows rows={3} />
        ) : terminals.error ? (
          <ErrorBlock
            message={serverMessage(
              terminals.error,
              "Não foi possível carregar as maquininhas."
            )}
            onRetry={terminals.refetch}
          />
        ) : !configured ? (
          <p
            role="status"
            className="rounded-xl bg-amber-50 px-3 py-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-200"
          >
            {POINT_SETUP_TEXT[info?.reason] ||
              "A cobrança na maquininha não está disponível agora."}
          </p>
        ) : (
          <>
            <p className="text-xs leading-snug text-slate-600">
              A maquininha precisa estar vinculada à conta Mercado Pago da Relinq
              (pelo app do Mercado Pago). Aqui você escolhe quais delas a Relinq
              pode cobrar e dá um apelido.
            </p>

            {registered.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200">
                <EmptyState
                  icon={MdOutlinePointOfSale}
                  title="Nenhuma maquininha cadastrada"
                  description="Sem maquininha cadastrada, o cartão só pode ser registrado à mão."
                />
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                {registered.map((terminal) => (
                  <li key={terminal.id} className="flex items-center gap-3 px-3 py-2.5">
                    <MdOutlinePointOfSale className="h-5 w-5 shrink-0 text-pink-600" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {terminal.name}
                      </p>
                      <p className="truncate text-[11px] text-slate-500">
                        {terminal.mpTerminalId}
                      </p>
                    </div>
                    <Badge tone={terminal.operatingMode === "PDV" ? "success" : "warning"}>
                      {terminal.operatingMode === "PDV" ? "Modo PDV" : terminal.operatingMode}
                    </Badge>
                    {removingId === terminal.id ? (
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          className="rounded-lg bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                          disabled={remove.isLoading}
                          onClick={() => remove.mutate(terminal.id)}
                        >
                          Confirmar
                        </button>
                        <button
                          type="button"
                          className="text-xs text-slate-500 underline"
                          onClick={() => setRemovingId(null)}
                        >
                          Voltar
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="text-xs font-semibold text-rose-700 hover:underline"
                        onClick={() => {
                          setMessage(null);
                          setError(null);
                          setRemovingId(terminal.id);
                        }}
                      >
                        Remover
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {!adding ? (
              <button
                type="button"
                className="rounded-xl bg-pink-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-pink-700"
                onClick={() => {
                  setMessage(null);
                  setError(null);
                  setAdding(true);
                }}
              >
                Cadastrar maquininha
              </button>
            ) : (
              <div className="space-y-3 rounded-2xl border border-pink-100 bg-pink-50/40 p-3">
                {available.isLoading ? (
                  <p className="text-xs text-slate-500">Buscando na conta do Mercado Pago...</p>
                ) : null}

                {!available.isLoading && !available.isError && choices.length === 0 ? (
                  <p className="text-xs text-slate-600">
                    Nenhuma maquininha livre na conta do Mercado Pago da Relinq.
                    Vincule a maquininha à conta pelo app do Mercado Pago e tente
                    de novo.
                  </p>
                ) : null}

                {choices.length > 0 ? (
                  <>
                    <div>
                      <label className={labelClass} htmlFor="platform-terminal">
                        Maquininha da conta
                      </label>
                      <select
                        id="platform-terminal"
                        value={selectedId}
                        onChange={(event) => {
                          setSelectedId(event.target.value);
                          setConfirmPdv(false);
                        }}
                        className={campoClass}
                      >
                        <option value="">Escolha uma maquininha</option>
                        {choices.map((item) => (
                          <option key={item.mpTerminalId} value={item.mpTerminalId}>
                            {item.mpTerminalId}
                            {item.operatingMode !== "PDV" ? " (fora do modo PDV)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    {chosen ? (
                      <>
                        <div>
                          <label className={labelClass} htmlFor="platform-terminal-name">
                            Apelido
                          </label>
                          <input
                            id="platform-terminal-name"
                            value={name}
                            maxLength={80}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Ex.: Balcão do evento"
                            className={campoClass}
                          />
                        </div>

                        {needsPdv ? (
                          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3">
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-400"
                              checked={confirmPdv}
                              onChange={(event) => setConfirmPdv(event.target.checked)}
                            />
                            <span className="text-xs leading-snug text-amber-900">
                              Esta maquininha vai ser colocada em modo PDV. Nesse
                              modo ela deixa de vender sozinha pelo app do Mercado
                              Pago e passa a receber só as cobranças do sistema.
                              Será preciso reiniciá-la depois.
                            </span>
                          </label>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : null}

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={!canRegister}
                    onClick={() => {
                      setError(null);
                      register.mutate();
                    }}
                    className="rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {register.isLoading ? "Cadastrando..." : "Salvar"}
                  </button>
                  <button
                    type="button"
                    className="text-xs text-slate-500 underline"
                    onClick={() => {
                      resetForm();
                      setError(null);
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {message ? (
          <p role="status" className="text-xs font-semibold text-emerald-700">
            {message}
          </p>
        ) : null}
        {error ? (
          <p
            role="alert"
            className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800 ring-1 ring-inset ring-rose-200"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default PlatformTerminalsModal;
